import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import ModalSide from '@/common/components/modal/ModalSide';
import { useBillingStore } from '@/store/useBillingStore';
import { billingService } from '@/services/billingService';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { PaymentRecordItem, PaymentRecordInvoice, RefundRequestItem } from '@/types/billing';

interface PaymentHistorySideProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  refundRequests?: RefundRequestItem[];
  onRefundRequest?: (paymentRecordId: number) => void;
}

const PaymentHistorySide: React.FC<PaymentHistorySideProps> = ({ isOpen, onClose, companyId, refundRequests = [], onRefundRequest }) => {
  const { payments, loadPayments, isPaymentsLoading } = useBillingStore();
  const paymentRecords = payments?.data ?? [];
  const [selectedItem, setSelectedItem] = useState<PaymentRecordItem | null>(null);

  useEffect(() => {
    if (isOpen && paymentRecords.length === 0) {
      loadPayments(companyId);
    }
  }, [isOpen, companyId, paymentRecords.length, loadPayments]);

  const groupedHistory = paymentRecords.reduce((groups, item) => {
    const dateStr = item.paidAt ?? item.createdAt;
    const month = dateStr.substring(0, 7);
    if (!groups[month]) {
      groups[month] = [];
    }
    groups[month].push(item);
    return groups;
  }, {} as Record<string, PaymentRecordItem[]>);

  const handleClose = () => {
    setSelectedItem(null);
    onClose();
  };

  return (
    <ModalSide
      isOpen={isOpen}
      onClose={handleClose}
      title={selectedItem ? '청구서 상세' : '결제 내역'}
      width="500px"
    >
      <Container>
        {isPaymentsLoading ? (
          <LoadingState><SpinIcon><Loader2 size={24} /></SpinIcon> 불러오는 중...</LoadingState>
        ) : selectedItem ? (
          <InvoiceDetailView
            item={selectedItem}
            onBack={() => setSelectedItem(null)}
            refundRequests={refundRequests}
            onRefundRequest={onRefundRequest}
          />
        ) : (
          <HistoryListView
            groupedHistory={groupedHistory}
            onSelect={setSelectedItem}
          />
        )}
      </Container>
    </ModalSide>
  );
};

const HistoryListView: React.FC<{
  groupedHistory: Record<string, PaymentRecordItem[]>;
  onSelect: (item: PaymentRecordItem) => void;
}> = ({ groupedHistory, onSelect }) => {
  const months = Object.keys(groupedHistory).sort().reverse();

  if (months.length === 0) {
    return <EmptyState>결제 내역이 없습니다.</EmptyState>;
  }

  return (
    <ListContainer>
      {months.map(month => (
        <MonthGroup key={month}>
          <MonthHeader>{month.split('-')[0]}년 {parseInt(month.split('-')[1])}월</MonthHeader>
          <MonthItems>
            {groupedHistory[month].map(item => (
              <HistoryItemCard key={item.id} onClick={() => onSelect(item)}>
                <ItemHeader>
                  <ItemDate>{(item.paidAt ?? item.createdAt).split('T')[0]}</ItemDate>
                  <StatusBadge $status={item.status}>
                    {item.status === 'SUCCESS' ? '성공' : '실패'}
                  </StatusBadge>
                </ItemHeader>
                <ItemBody>
                  <ItemDesc>{item.title}</ItemDesc>
                  <ItemAmount>{item.amount.toLocaleString()}원</ItemAmount>
                </ItemBody>
              </HistoryItemCard>
            ))}
          </MonthItems>
        </MonthGroup>
      ))}
    </ListContainer>
  );
};

const InvoiceDetailView: React.FC<{
  item: PaymentRecordItem;
  onBack: () => void;
  refundRequests?: RefundRequestItem[];
  onRefundRequest?: (paymentRecordId: number) => void;
}> = ({ item, onBack, refundRequests = [], onRefundRequest }) => {
  const hasRefundRequest = refundRequests.some(r => r.paymentRecordId === item.id);
  const existingRefund = refundRequests.find(r => r.paymentRecordId === item.id);
  const [invoice, setInvoice] = useState<PaymentRecordInvoice | null>(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      setIsLoadingInvoice(true);
      try {
        const data = await billingService.getInvoice(item.invoiceId);
        setInvoice(data);
      } catch {
        setInvoice(null);
      } finally {
        setIsLoadingInvoice(false);
      }
    };
    fetchInvoice();
  }, [item.invoiceId]);

  return (
    <DetailContainer>
      <BackButton onClick={onBack}>
        <ChevronLeft size={16} /> 목록으로 돌아가기
      </BackButton>

      <DetailHeader>
        <DetailTitle>{item.amount.toLocaleString()}원</DetailTitle>
        <StatusBadge $status={item.status} className="large">
          {item.status === 'SUCCESS' ? '결제 완료' : '결제 실패'}
        </StatusBadge>
      </DetailHeader>

      <DetailSection>
        <DetailRow>
          <DetailLabel>결제 일시</DetailLabel>
          <DetailValue>{(item.paidAt ?? item.createdAt).replace('T', ' ').split('.')[0]}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>결제 유형</DetailLabel>
          <DetailValue>{item.title}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>주문 ID</DetailLabel>
          <DetailValue>{item.orderId}</DetailValue>
        </DetailRow>
      </DetailSection>

      {isLoadingInvoice ? (
        <LoadingState><SpinIcon><Loader2 size={20} /></SpinIcon> 청구서 불러오는 중...</LoadingState>
      ) : invoice ? (
        <InvoiceTable>
          <TableHeader>
            <Tr>
              <Th>항목</Th>
              <Th align="center">수량</Th>
              <Th align="right">단가</Th>
              <Th align="right">금액</Th>
            </Tr>
          </TableHeader>
          <TableBody>
            {invoice.lines.map((line, idx) => (
              <Tr key={idx}>
                <Td>{line.description ?? '-'}</Td>
                <Td align="center">{line.quantity}</Td>
                <Td align="right">{line.unitPrice.toLocaleString()}{line.amountUnit}</Td>
                <Td align="right">{line.amount.toLocaleString()}{line.amountUnit}</Td>
              </Tr>
            ))}
          </TableBody>
          <TableFooter>
            <Tr>
              <Th colSpan={3}>합계</Th>
              <Th align="right" className="total">{invoice.totalAmount.toLocaleString()}원</Th>
            </Tr>
          </TableFooter>
        </InvoiceTable>
      ) : (
        <InvoiceTable>
          <TableHeader>
            <Tr>
              <Th>항목</Th>
              <Th align="right">금액</Th>
            </Tr>
          </TableHeader>
          <TableBody>
            <Tr>
              <Td>{item.title}</Td>
              <Td align="right">{item.amount.toLocaleString()}원</Td>
            </Tr>
          </TableBody>
          <TableFooter>
            <Tr>
              <Th>합계</Th>
              <Th align="right" className="total">{item.amount.toLocaleString()}원</Th>
            </Tr>
          </TableFooter>
        </InvoiceTable>
      )}

      {item.status === 'SUCCESS' && item.receiptUrl && (
        <ReceiptButton onClick={() => window.open(item.receiptUrl, '_blank')}>
          영수증 조회
        </ReceiptButton>
      )}

      {item.status === 'SUCCESS' && !hasRefundRequest && onRefundRequest && (
        <RefundRequestButton onClick={() => onRefundRequest(item.id)}>
          환불 신청
        </RefundRequestButton>
      )}

      {existingRefund && (
        <RefundStatusInfo $status={existingRefund.status}>
          환불 {existingRefund.status === 'PENDING' ? '처리 대기 중' : existingRefund.status === 'APPROVED' ? '승인됨' : '거절됨'}
          {existingRefund.requestedAmount > 0 && ` (${existingRefund.requestedAmount.toLocaleString()}원)`}
        </RefundStatusInfo>
      )}
    </DetailContainer>
  );
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${semanticColors.background.primary};
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 2rem;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
`;

const SpinIcon = styled.span`
  display: flex;
  animation: ${spin} 1s linear infinite;
`;

const EmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.default};
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MonthGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const MonthHeader = styled.div`
  padding: 0.75rem 1.5rem;
  background: ${semanticColors.background.secondary};
  color: ${semanticColors.text.secondary};
  ${typography.caption.caption1.default};
  font-weight: 600;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
  border-top: 1px solid ${semanticColors.border.tertiary};
  
  &:first-child {
    border-top: none;
  }
`;

const MonthItems = styled.div`
  display: flex;
  flex-direction: column;
`;

const HistoryItemCard = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${semanticColors.background.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ItemDate = styled.div`
  color: ${semanticColors.text.secondary};
  ${typography.caption.caption1.default};
`;

const ItemBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemDesc = styled.div`
  color: ${semanticColors.text.primary};
  ${typography.paragraph.p1.default};
  font-weight: 500;
`;

const ItemAmount = styled.div`
  color: ${semanticColors.text.primary};
  ${typography.heading.h6};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  ${typography.caption.caption1.default};
  font-weight: 600;

  &.large {
    ${typography.paragraph.p2.default};
    padding: 0.375rem 0.75rem;
  }

  background: ${props => {
    switch (props.$status) {
      case 'SUCCESS': return '#10b98115';
      case 'FAILED': return '#ef444415';
      default: return '#f59e0b15';
    }
  }};
  
  color: ${props => {
    switch (props.$status) {
      case 'SUCCESS': return '#10b981';
      case 'FAILED': return '#ef4444';
      default: return '#f59e0b';
    }
  }};
`;

const DetailContainer = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
  cursor: pointer;
  padding: 0;
  width: fit-content;

  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const DetailTitle = styled.h2`
  ${typography.heading.h3};
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.div`
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
`;

const DetailValue = styled.div`
  color: ${semanticColors.text.primary};
  ${typography.paragraph.p1.default};
  font-weight: 500;
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.thead`
  border-bottom: 2px solid ${semanticColors.border.primary};
`;

const TableBody = styled.tbody`
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const TableFooter = styled.tfoot`
  background: ${semanticColors.background.secondary};
`;

const Tr = styled.tr``;

const Th = styled.th<{ align?: string; colSpan?: number }>`
  text-align: ${props => props.align || 'left'};
  padding: 1rem;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
  font-weight: 600;

  &.total {
    color: ${semanticColors.text.primary};
    ${typography.heading.h6};
  }
`;

const Td = styled.td<{ align?: string }>`
  text-align: ${props => props.align || 'left'};
  padding: 1.5rem 1rem;
  color: ${semanticColors.text.primary};
  ${typography.paragraph.p1.default};
`;

const ReceiptButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: white;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  color: ${semanticColors.text.primary};
  ${typography.paragraph.p1.default};
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;

  &:hover {
    background: ${semanticColors.background.secondary};
  }
`;

const RefundRequestButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: white;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  color: #dc2626;
  ${typography.paragraph.p1.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #f87171;
  }
`;

const REFUND_STATUS_BG: Record<string, string> = {
  PENDING: '#fef9c3',
  APPROVED: '#dcfce7',
  REJECTED: '#fee2e2',
};

const REFUND_STATUS_COLOR: Record<string, string> = {
  PENDING: '#a16207',
  APPROVED: '#15803d',
  REJECTED: '#dc2626',
};

const RefundStatusInfo = styled.div<{ $status: string }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  ${typography.paragraph.p2.default};
  font-weight: 600;
  text-align: center;
  background: ${(p) => REFUND_STATUS_BG[p.$status] ?? '#f3f4f6'};
  color: ${(p) => REFUND_STATUS_COLOR[p.$status] ?? '#6b7280'};
`;

export default PaymentHistorySide;
